import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch } from '../../apiClient';
import './BookDNACard.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function BookDNACard({ factFictionMode, navigateTo }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [dna, setDna] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notReady, setNotReady] = useState(false);

  const fetchDNA = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      const res = await apiFetch(instance, '/api/gamification/dna');
      if (res.status === 404) {
        setNotReady(true);
        return;
      }
      if (!res.ok) throw new Error('Failed to load Book DNA');
      const data = await res.json();
      if (!data || data.notReady) {
        setNotReady(true);
      } else {
        setDna(data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load Book DNA');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchDNA(); }, [fetchDNA]);

  if (loading) return <div className="bookdna-loading">{t('bookClub.bookDNA.loading')}</div>;
  if (error) return <div className="bookdna-error">{error} <button onClick={fetchDNA}>{t('bookClub.common.retry')}</button></div>;

  if (notReady) {
    return (
      <div className="bookdna-container">
        <div className="bookdna-not-ready">
          <span className="bookdna-not-ready-emoji" role="img" aria-label="dna">🧬</span>
          <h3 className="bookdna-not-ready-title">{t('bookClub.bookDNA.discoverTitle')}</h3>
          <p className="bookdna-not-ready-desc">{t('bookClub.bookDNA.discoverDesc')}</p>
          {navigateTo && (
            <div>
              <button className="bookclub-nav-link" onClick={() => navigateTo('search')}>🔎 {t('bookClub.common.searchBooks')}</button>{' '}
              <button className="bookclub-nav-link" onClick={() => navigateTo('my-books')}>📖 {t('bookClub.common.goToMyBooks')}</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const readerType = dna.readerType || 'Reader';
  const genres = unwrap(dna.genres || dna.genreBreakdown);
  const difficulty = dna.difficulty ?? 0;
  const pace = dna.pace || dna.paceLabel || 'Moderate';
  const factRatio = dna.factRatio ?? 50;
  const fictionRatio = 100 - factRatio;
  const favoriteMood = dna.favoriteMood || dna.mood;
  const maxGenreScore = genres.length > 0 ? Math.max(...genres.map(g => g.score ?? g.count ?? 1)) : 1;

  return (
    <div className="bookdna-container">
      <div className="bookdna-card">
        {/* Reader Type Header */}
        <div className="bookdna-header">
          <span className="bookdna-emoji" role="img" aria-label="dna">🧬</span>
          <h2 className="bookdna-reader-type">{readerType}</h2>
        </div>

        {/* Genre Breakdown (bar chart) */}
        {genres.length > 0 && (
          <div className="bookdna-section">
            <h4 className="bookdna-section-title">{t('bookClub.bookDNA.genreRadar')}</h4>
            <div className="bookdna-genre-bars">
              {genres.map((g, i) => (
                <div key={g.name || i} className="bookdna-genre-row">
                  <span className="bookdna-genre-label">{g.name || g.genre}</span>
                  <div className="bookdna-genre-bar-track">
                    <div
                      className="bookdna-genre-bar-fill"
                      style={{ width: `${((g.score ?? g.count ?? 0) / maxGenreScore) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="bookdna-stats-row">
          {/* Difficulty stars */}
          <div className="bookdna-stat">
            <span className="bookdna-stat-label">{t('bookClub.bookDNA.difficulty')}</span>
            <span className="bookdna-difficulty-stars">
              {[1, 2, 3, 4, 5].map(s => (
                <span key={s} className={s <= difficulty ? 'star-filled' : 'star-empty'}>★</span>
              ))}
            </span>
          </div>

          {/* Pace badge */}
          <div className="bookdna-stat">
            <span className="bookdna-stat-label">{t('bookClub.bookDNA.pace')}</span>
            <span className="bookdna-pace-badge">{pace}</span>
          </div>
        </div>

        {/* Fact : Fiction ratio bar */}
        <div className="bookdna-section">
          <h4 className="bookdna-section-title">{t('bookClub.bookDNA.factFiction')}</h4>
          <div className="bookdna-ratio-bar">
            <div className="bookdna-ratio-fact" style={{ width: `${factRatio}%` }}>
              {factRatio > 10 && <span>{factRatio}%</span>}
            </div>
            <div className="bookdna-ratio-fiction" style={{ width: `${fictionRatio}%` }}>
              {fictionRatio > 10 && <span>{fictionRatio}%</span>}
            </div>
          </div>
          <div className="bookdna-ratio-labels">
            <span>📚 {t('bookClub.fact')}</span>
            <span>📖 {t('bookClub.fiction')}</span>
          </div>
        </div>

        {/* Favorite mood */}
        {favoriteMood && (
          <div className="bookdna-mood">
            <span className="bookdna-mood-label">{t('bookClub.bookDNA.favoriteMood')}</span>
            <span className="bookdna-mood-tag">{favoriteMood}</span>
          </div>
        )}

        <p className="bookdna-evolve-note">{t('bookClub.bookDNA.evolveNote')}</p>
      </div>
    </div>
  );
}

export default BookDNACard;
